import type {ReactElement} from 'react';
import React from 'react';
import {BaseTextOutlineOptions} from '@Panels/poster-editor-text-outline-panel/components/base-text-outline-options';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromTextStylesOfSelectedTextSlideItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesOfSelectedTextSlideItem';
import {useGetKeyFromSelectedTextSlideItem} from '@Hooks/poster-editor/useGetKeyFromSelectedTextSlideItem';

interface TextSlideOutlineOptionsProps {
  panelId: string;
  onClickColorFill(): void;
}

export function TextSlideOutlineOptions({...props}: TextSlideOutlineOptionsProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const stroke = useGetKeyFromTextStylesOfSelectedTextSlideItem('stroke');
  const isBold = useGetKeyFromTextStylesOfSelectedTextSlideItem('isBold');
  const strokeWidth = useGetKeyFromTextStylesOfSelectedTextSlideItem('strokeWidth');
  const strokeColor = useGetKeyFromTextStylesOfSelectedTextSlideItem('strokeColor');
  const scaleX = useGetKeyFromSelectedTextSlideItem('scaleX');

  if (!activeItem?.isSlideshow()) {
    return null;
  }

  if (!activeSlide?.isTextSlide() || activeSlideUID === undefined) {
    return null;
  }

  if (!strokeColor || strokeWidth === undefined || isBold === undefined || stroke === undefined || scaleX === undefined) {
    return null;
  }

  const onToggleOutlineOption = (): void => {
    void activeItem.updateSlideFromObject(activeSlideUID, {
      textStyles: {
        stroke: !stroke,
        strokeWidth: activeSlide.textStyles.getDefaultStrokeWidth(ITEM_TYPE.TEXTSLIDE, scaleX),
        strokeColor: activeSlide.textStyles.getColorForStroke(),
      },
    });
  };

  const updateStrokeThickness = (strokeWidth: number, undoable = true): void => {
    void activeItem.updateSlideFromObject(
      activeSlide.uid,
      {
        textStyles: {
          strokeWidth,
        },
      },
      {
        undoable,
      }
    );
  };

  return (
    <BaseTextOutlineOptions
      key={String(isBold)}
      panelId={props.panelId}
      stroke={stroke}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      isDisabled={activeSlide.isOutlineDisabled()}
      onClickColorFill={(): void => {
        props.onClickColorFill();
      }}
      onToggleStroke={onToggleOutlineOption}
      updateStrokeThickness={updateStrokeThickness}
    />
  );
}
